import { calculateSmsSegments } from "@/lib/sms-segments";

interface Props {
	/** Lien court sous sa forme SMS (`akwa.ba/a3k9mz7`). */
	shortForm: string;
	/** Destination complète, pour la comparaison. */
	longUrl: string;
	/** Message type dans lequel le lien sera inséré. */
	template?: string;
}

/**
 * Aperçu du message tel qu'il partira.
 *
 * Deux lignes de comparaison, et surtout un avertissement en rouge dès que le
 * texte bascule en UCS-2 : c'est le seul endroit du produit où l'on voit, avant
 * l'envoi, qu'une apostrophe typographique vient de doubler la facture d'une
 * campagne.
 */
export function SmsPreview({ shortForm, longUrl, template }: Props) {
	const base = template?.trim() || "Akwaba : commande AKW-2026-000123 en route ! Suivi :";
	const withShort = `${base} ${shortForm}`;
	const withLong = longUrl ? `${base} ${longUrl}` : "";

	const shortMetrics = calculateSmsSegments(withShort);
	const longMetrics = withLong ? calculateSmsSegments(withLong) : null;
	const saved = longMetrics ? Math.max(longMetrics.segments - shortMetrics.segments, 0) : 0;

	return (
		<div
			style={{
				border: "1px solid var(--a-border)",
				borderRadius: "var(--a-r)",
				background: "var(--a-surface-2)",
				padding: 14,
				fontSize: 12.5,
			}}
		>
			<div
				className="mono"
				style={{
					background: "var(--a-surface)",
					border: "1px solid var(--a-border)",
					borderRadius: "var(--a-r-sm)",
					padding: "10px 12px",
					marginBottom: 10,
					lineHeight: 1.5,
					wordBreak: "break-word",
				}}
			>
				{withShort}
			</div>

			<div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
				<span className="tn">
					<strong>{shortMetrics.length}</strong> caractères
				</span>
				<span className="tn">
					<strong>{shortMetrics.segments}</strong>{" "}
					{shortMetrics.segments > 1 ? "segments" : "segment"}
				</span>
				<span className="tn">{shortMetrics.costFcfa} F</span>
				<span className={shortMetrics.isUcs2 ? "" : "muted"}>
					{shortMetrics.isUcs2 ? "UCS-2" : "GSM-7"}
				</span>
				<span className="muted tn">
					encore {shortMetrics.remaining} car. sur ce segment
				</span>
			</div>

			{longMetrics ? (
				<div className="muted" style={{ marginTop: 8 }}>
					Avec l'URL longue : {longMetrics.length} caractères ·{" "}
					{longMetrics.segments} {longMetrics.segments > 1 ? "segments" : "segment"}
					{saved > 0 ? (
						<strong style={{ color: "var(--a-success)" }}>
							{" "}
							— {saved} segment{saved > 1 ? "s" : ""} économisé{saved > 1 ? "s" : ""}
						</strong>
					) : (
						/* Dire la vérité plutôt que gonfler un indicateur : sur un message
						   court, le lien long tient déjà dans un segment. La valeur du lien
						   court est ailleurs — il fonctionne, il se re-pointe, il se mesure. */
						<span> — même nombre de segments, le gain est la mesure et la souplesse</span>
					)}
				</div>
			) : null}

			{shortMetrics.isUcs2 ? (
				<div
					style={{
						marginTop: 10,
						padding: "8px 10px",
						borderRadius: "var(--a-r-sm)",
						background: "var(--a-danger-soft)",
						color: "var(--a-danger)",
						fontWeight: 600,
					}}
				>
					Ce message bascule en UCS-2 : le segment tombe de 160 à 70 caractères et le
					coût double.
					{shortMetrics.offenders.length > 0 ? (
						<>
							{" "}
							En cause :{" "}
							<span className="mono">{shortMetrics.offenders.join(" ")}</span>
						</>
					) : null}
				</div>
			) : null}
		</div>
	);
}
